import React, { useEffect, useState } from "react";
import { Card, Col, Row, Select } from "antd";
import { toast } from "react-toastify";
import { Controller } from "react-hook-form";
import dayjs from "dayjs";
import styles from "./create-exam.module.css";
import { createAntdSelection } from "../../../../utils/common";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import CustomSelect from "../../../../common/input/custom-select/custom-select";
import CustomDatepicker from "../../../../common/input/custom-datepicker/custom-datepicker";
import CustomButton from "../../../../common/custom-button/custom-button";
import { getExamType } from "../../../../services/examination";

const CreateExam = ({ form, handleSaveExam, termList }) => {
  const { control, watch, handleSubmit, formState } = form;
  const termId = watch("term_id");
  const [loading, setLoading] = useState(false);
  const [examType, setExamType] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);

  useEffect(() => {
    if (termId) {
      const selectedTermObject = termList?.find((e) => e?.id === termId);
      setSelectedTerm(selectedTermObject);
    } else setSelectedTerm(null);
  }, [termId, termList]);

  useEffect(() => {
    fetchExamTypeList();
  }, []);

  const fetchExamTypeList = async () => {
    try {
      setLoading(true);
      const resp = await getExamType();
      const data = resp?.data?.data?.rows;
      setExamType(createAntdSelection(data, "term"));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const submitForm = (values) => {
    const startDate = dayjs(values.exam_start_date);
    const endDate = dayjs(values.exam_end_date);

    const termStart = dayjs(selectedTerm.start_date, "DD-MM-YYYY");
    const termEnd = dayjs(selectedTerm.end_date, "DD-MM-YYYY");

    // Start date outside term
    if (startDate.isBefore(termStart, "day")) {
      toast.error(
        `Exam start date cannot be before ${termStart.format("DD-MM-YYYY")}`
      );
      return;
    }

    if (startDate.isAfter(termEnd, "day")) {
      toast.error(
        `Exam start date cannot be after ${termEnd.format("DD-MM-YYYY")}`
      );
      return;
    }

    // End date outside term
    if (endDate.isBefore(termStart, "day")) {
      toast.error(
        `Exam end date cannot be before ${termStart.format("DD-MM-YYYY")}`
      );
      return;
    }

    if (endDate.isAfter(termEnd, "day")) {
      toast.error(
        `Exam end date cannot be after ${termEnd.format("DD-MM-YYYY")}`
      );
      return;
    }

    // Start > End
    if (startDate.isAfter(endDate, "day")) {
      toast.error("Exam start date cannot be greater than exam end date");
      return;
    }

    handleSaveExam(values);
  };

  return (
    <Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <Controller
            name="term_id"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomSelect
                id={"term_id"}
                size="large"
                labelColor="#171717"
                placeholder="--Select Term--"
                label="Select Term"
                showRequired
                loading={loading}
                value={value}
                onChange={onChange}
                disabled
                status={error ? "error" : ""}
              >
                {termList?.map((option, index) => (
                  <Select.Option key={option?.id || index} value={option?.id}>
                    {option?.name}
                  </Select.Option>
                ))}
              </CustomSelect>
            )}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <Controller
            name="exam_name"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomTextfield
                id="exam_name"
                label="Exam Name"
                labelColor="#434343"
                placeholder="Enter exam name"
                size="large"
                value={value}
                onChange={onChange}
                showRequired
                status={error ? "error" : ""}
              />
            )}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <Controller
            name="exam_type"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomSelect
                labelColor="#171717"
                placeholder="Select Exam Type"
                label="Select Exam Type"
                showSearch
                size="large"
                showRequired
                loading={loading}
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
              >
                {examType?.map((option, index) => (
                  <Select.Option key={index} value={option?.value}>
                    {option?.label}
                  </Select.Option>
                ))}
              </CustomSelect>
            )}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <Controller
            name="exam_start_date"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomDatepicker
                id="exam_start_date"
                label="Exam start date"
                labelColor="#171717"
                showRequired
                placeholder="DD-MM-YYYY"
                size="large"
                value={value}
                onChange={onChange}
                style={{ height: "40px", width: "100%" }}
                status={error ? "error" : ""}
                prefix={
                  <img
                    src="/assets/images/calendarOutlined.svg"
                    style={{ marginRight: 4 }}
                    alt="calendar"
                  />
                }
              />
            )}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <Controller
            name="exam_end_date"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomDatepicker
                id="exam_end_date"
                label="Exam end date"
                labelColor="#171717"
                placeholder="DD-MM-YYYY"
                size="large"
                showRequired
                value={value}
                onChange={onChange}
                style={{ height: "40px", width: "100%" }}
                status={error ? "error" : ""}
                prefix={
                  <img
                    src="/assets/images/calendarOutlined.svg"
                    style={{ marginRight: 4 }}
                    alt="calendar"
                  />
                }
              />
            )}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <Controller
            name="max_slots"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomSelect
                labelColor="#171717"
                placeholder="Max slots per day"
                label="Max slots per day"
                showSearch
                size="large"
                showRequired
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
              >
                {Array.from({ length: 20 }, (_, i) => i + 1)?.map(
                  (option, index) => (
                    <Select.Option key={index} value={option}>
                      {option}
                    </Select.Option>
                  )
                )}
              </CustomSelect>
            )}
          />
        </Col>
        <Col xs={24} sm={24} md={24} lg={6} xl={4}>
          <CustomButton
            size="large"
            onClick={handleSubmit(submitForm)}
            title="Save"
            disabled={!formState.isDirty}
            className={styles.saveButton}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default CreateExam;
